/* eslint-disable no-param-reassign */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BREAKPOINT_INFINITY } from 'common/constants/cap-table/capTable';
import { CONVERTIBLE_NOTES, UNISSUED_OPTIONS } from 'common/constants/securityTypes';
import { Currency } from 'common/types/format';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { MIN_CUSTOM_BREAKPOINT_COLUMNS } from '../../constants';
import { BreakpointColumn, UseCustomBreakpointsTableData } from '../../types/breakpoint-analysis.models';
import {
  alignSeriesToTemplate,
  getBreakpointRange,
} from '../../utilities/customBreakpointColumns';
import { conditions, customValidations, parser, reverseParser, rowConfig } from '../common';

const useCustomBreakpointsTableData = ({ customBreakpointColumns, captableInfo, format, isDisabled }: UseCustomBreakpointsTableData): {
  spreadsheet: SpreadsheetConfig | null;
  addCustomColumn: () => void;
  // Matches ScalarSpreadsheet deleteColumn signature
  deleteCustomColumn: (
    columnIndex: number,
    columnId?: string | number,
    workbook?: unknown,
    onDeleteComplete?: () => void
  ) => void;
} => {
  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetConfig | null>(null);
  const { currency } = format;
  const { symbol: currencySymbol, code: currencyCode } = currency as Currency;
  const [columnsCustomBP, setColumnsCustomBP] = useState<BreakpointColumn[]>(customBreakpointColumns);

  const config = useMemo(() => {
    if (columnsCustomBP) {
      return rowConfig({ data: columnsCustomBP, isCustomBreakpoint: true });
    }
    return null;
  }, [columnsCustomBP]);

  const renumberAndRerangeColumns = useCallback(
    (columns: BreakpointColumn[]): BreakpointColumn[] => {
      const alphabet = alphabetGenerator([], columns.length + 1);

      return columns.map((bp, bpIndex) => {
        const isLastColumn = bpIndex === columns.length - 1;
        return {
          ...bp,
          id: bpIndex + 1,
          name: `Breakpoint ${bpIndex + 1}`,
          range: getBreakpointRange({
            columnIndex: bpIndex,
            columns,
            alphabet,
            currencySymbol: currencySymbol ?? '',
            currency: currency as Currency,
          }),
          price: isLastColumn ? BREAKPOINT_INFINITY : bp.price,
          // Finite columns only, and only when more than the required minimum remain
          isDeleteableColumn: !isLastColumn && columns.length > MIN_CUSTOM_BREAKPOINT_COLUMNS,
        };
      });
    },
    [currency, currencySymbol]
  );

  useEffect(() => {
    const updatedCustomBreakpoints = columnsCustomBP || customBreakpointColumns;
    if (updatedCustomBreakpoints?.length) {
      const sizeColumns = updatedCustomBreakpoints.length;
      const seriesTemplate = updatedCustomBreakpoints[0]?.series ?? [];
      const columnsData = updatedCustomBreakpoints.map((bp, bpIndex) => {
        const isLastColumn = bpIndex === sizeColumns - 1;
        // Finite columns only, and only when more than the required minimum remain
        const isDeleteableColumn = bpIndex < sizeColumns - 1 && sizeColumns > MIN_CUSTOM_BREAKPOINT_COLUMNS;
        return {
          ...bp,
          id: bpIndex + 1,
          price: isLastColumn ? BREAKPOINT_INFINITY : bp.price,
          series: alignSeriesToTemplate(seriesTemplate, bp.series),
          isDeleteableColumn,
        };
      });
      setColumnsCustomBP(columnsData);
    }
  }, [customBreakpointColumns, columnsCustomBP?.length]);

  const tableTerms = useMemo(
    () => ({
      tableName: 'Custom Breakpoints',
      tableSlug: 'custom-breakpoints',
      columnName: 'custom breakpoint',
    }),
    []
  );

  // Use this template when the only breakpoint is the Infinity one
  const breakpointTemplate = useMemo((): BreakpointColumn => {
    if (!captableInfo) {
      return {} as BreakpointColumn;
    }
    const series = captableInfo?.securities
      ?.filter(security => ![UNISSUED_OPTIONS, CONVERTIBLE_NOTES].includes(security.security_type ?? -1))
      .map(security => ({
        id: security.id,
        security: security.name,
        value: 0,
      }));

    if (!series) {
      return {} as BreakpointColumn;
    }

    return {
      id: 1,
      name: 'Breakpoint 1',
      price: 0,
      range: `${currencySymbol}0 to ${currencySymbol}B`,
      series,
      total: 0,
    } as BreakpointColumn;
  }, [captableInfo]);

  useEffect(() => {
    if (columnsCustomBP && tableTerms) {
      const spreadsheetConfig = new SpreadsheetConfig({
        parser,
        reverseParser,
        rowConfig: config,
        name: 'BreakPoints',
        columns: columnsCustomBP,
        tableData: {
          currencySymbol,
          columns: columnsCustomBP,
          isCustomBreakpointsTable: true,
          isDisabled,
        },
        currency: `${currencyCode} ${currencySymbol}`,
        tableTerms,
        showTotalColumn: false,
        showToolbar: false,
        showPreviousColsDivider: false,
        hasColTitle: true,
        allowConfirmAndDeleteColumn: true,
        allowDeleteOnlySpecificColumn: true,
        disableDeleteBtnOnFirstColumn: false,
        currencyFormatter: false,
        unitsFormatter: false,
        format,
        customValidations,
        conditions,
      } as SpreadsheetConfig);

      setSpreadsheet(spreadsheetConfig);
    }
  }, [columnsCustomBP, tableTerms, isDisabled]);

  const addCustomColumn = useCallback(() => {
    if (!columnsCustomBP?.length) {
      return;
    }

    const previousColumns = columnsCustomBP;
    const firstColumn = previousColumns[0];
    const lastColumn = previousColumns[previousColumns.length - 1];
    // rowConfig titles come from the first column's series — every column must match that length/order
    const seriesTemplate = firstColumn?.series ?? lastColumn.series;

    const newFiniteColumn = {
      id: previousColumns.length,
      name: `Breakpoint ${previousColumns.length}`,
      price: 0,
      total: 0,
      range: '',
      series: alignSeriesToTemplate(seriesTemplate, seriesTemplate, { resetValues: true }),
    } as BreakpointColumn;

    // Preserve Infinity allocation weights by id, but keep series aligned to the title row template
    const nextInfinityColumn = {
      ...lastColumn,
      series: alignSeriesToTemplate(seriesTemplate, lastColumn.series),
    } as BreakpointColumn;

    const nextColumns = [
      ...previousColumns.slice(0, -1),
      previousColumns.length > 1 ? newFiniteColumn : breakpointTemplate,
      nextInfinityColumn,
    ];

    setColumnsCustomBP(renumberAndRerangeColumns(nextColumns));
  }, [breakpointTemplate, columnsCustomBP, renumberAndRerangeColumns]);

  // ScalarSpreadsheet always invokes deleteColumn(columnIndex, columnId, workbook, onDeleteComplete).
  // Custom breakpoints only need the index and the completion callback; columnId/workbook are unused
  // but must remain as positional placeholders so onDeleteComplete is received as the 4th argument.
  const deleteCustomColumn = useCallback(
    (
      columnIndex: number,
      _columnId?: string | number,
      _workbook?: unknown,
      // hideIndicator from ColumnIndicator — clears the column menu after delete
      onDeleteComplete?: () => void
    ) => {
      const availableColumns = columnsCustomBP ?? [];
      // Guard: invalid index, Infinity column, or would drop below one finite + Infinity
      if (
        columnIndex < 0
        || columnIndex >= availableColumns.length - 1
        || availableColumns.length <= MIN_CUSTOM_BREAKPOINT_COLUMNS
      ) {
        return;
      }

      const remaining = [...availableColumns.slice(0, columnIndex), ...availableColumns.slice(columnIndex + 1)];
      setColumnsCustomBP(renumberAndRerangeColumns(remaining));
      onDeleteComplete?.();
    },
    [columnsCustomBP, renumberAndRerangeColumns]
  );

  return {
    spreadsheet,
    addCustomColumn,
    deleteCustomColumn,
  };
};

export default useCustomBreakpointsTableData;
